// Canonical (source) dictionary for the public marketing site.
// `MktDict` is derived from this object, so every other locale must implement
// the exact same shape — missing keys are TypeScript errors.
//
// NOTE: small decorative / technical micro-text inside illustrations (the
// pipeline SVG node labels, the bakery demo mock-content, the studio/review
// surface mockups) is intentionally NOT localized here — it stays as English
// chrome in Landing.tsx.

export const en = {
  nav: {
    items: {
      pricing: "Pricing",
      examples: "Examples",
      templates: "Library",
      design: "Design",
      docs: "Docs",
      developers: "Developers",
      blog: "Blog",
      about: "About",
    },
    openStudio: "Open studio →",
    themeToggle: "Toggle theme",
    language: "Language",
    status: "Status",
    signIn: "Sign in",
    menu: "Menu",
  },

  footer: {
    tagline: "Turn intent and source material into living apps and documents.",
    groups: {
      product: {
        title: "Product",
        links: {
          pricing: "Pricing",
          templates: "Library",
          docs: "Docs",
          designSystem: "Design system",
        },
      },
      company: {
        title: "Company",
        links: { about: "About", blog: "Blog", contact: "Contact" },
      },
      compare: {
        title: "Compare",
        links: { framer: "vs Framer", wix: "vs Wix", v0: "vs V0" },
      },
      legal: {
        title: "Legal",
        links: { privacy: "Privacy", terms: "Terms", security: "Security" },
      },
    },
    // Rendered as "© {year} {copyright}".
    copyright: "coogo · PADO Software Design L.L.C.",
    location: "Dubai, UAE",
  },

  // Legacy homepage footer (homepage/components/Footer.tsx) — distinct group/link set
  // from the marketing footer above (adds Resources + Legal + Studio/Careers/Refund/etc.).
  homeFooter: {
    groups: {
      product: {
        title: "Product",
        links: {
          templates: "Library",
          designSystem: "Design system",
          pricing: "Pricing",
          studio: "Studio",
          howItWorks: "How it Works",
        },
      },
      resources: {
        title: "Resources",
        links: {
          documentation: "Documentation",
          blog: "Blog",
          apiReference: "API Reference",
          templates: "Library",
        },
      },
      company: {
        title: "Company",
        links: { aboutUs: "About Us", contact: "Contact", careers: "Careers" },
      },
      legal: {
        title: "Legal",
        links: { terms: "Terms", privacy: "Privacy", refund: "Refund" },
      },
    },
  },

  hero: {
    badge: "coogo Studio",
    h1: {
      line1: "Living applications.",
      line2: "Living documents",
      accent: ".",
    },
    subhead:
      "Describe what you want to make or change. coogo works with your sources, proposes visible changes, and keeps the app, document, or report ready to review, share, and export.",
    promptLines: [
      "A source-backed campaign report with review notes and export state…",
      "A clinic appointment app — clients book, staff get SMS reminders…",
      "A members-only podcast with monthly subscription and dark mode…",
      "A waitlist for my cat-sitting service with a referral leaderboard…",
    ],
    inputPlaceholder:
      "Ask coogo to make an onboarding app, transform a deck, or revise a report...",
    generate: "Ask coogo",
    chips: ["App", "Document", "Report", "Review loop"],
  },

  why: {
    label: "01 / product",
    h2: "A workspace for user intent and coogo agents.",
    intro:
      "coogo is not a static page generator. It turns intent and source material into artifacts that remain reviewable, changeable, and trusted.",
    card1: {
      chip: "applications",
      h3: "Applications stay live.",
      body: "Ask for a product surface, internal tool, or mobile workflow. coogo opens the running app in Studio so every follow-up lands on the visible artifact.",
      annLeft: "intent",
      annMid: "same artifact",
      annRight: "app",
    },
    card2: {
      chip: "documents",
      h3: "Documents stay editable.",
      body: "Upload files, ask for structure, then revise through proposals instead of silent mutation. The document remains a working object, not a one-time AI draft.",
    },
    card3: {
      chip: "reports",
      h3: "Reports stay source-backed.",
      body: "Charts, tables, text, and exports stay connected to source coverage and verification checks, so trust is visible before you share.",
      link: "See the system ->",
    },
  },

  how: {
    label: "02 / workflow",
    h2: "Ask, review, approve, export.",
    intro:
      "The default loop is simple: ask coogo, inspect the artifact, decide on the suggested change, then publish, share, or export.",
    steps: [
      {
        n: "01",
        title: "Ask coogo",
        body: "Describe the outcome or attach source material. The request stays with the Studio handoff instead of becoming URL clutter.",
        caption: "intent captured",
      },
      {
        n: "02",
        title: "See the artifact",
        body: "The app, document, or report becomes the center surface. Source and checks stay available, but they do not displace the work.",
        caption: "artifact first",
      },
      {
        n: "03",
        title: "Decide",
        body: "coogo proposes the change in plain language. Use it, revise it, or keep the current version before the artifact changes.",
        caption: "use / revise / keep",
      },
      {
        n: "04",
        title: "Share or export",
        body: "Publish apps, share previews, or export documents and reports with target-specific readiness and verification context.",
        caption: "publish / share / export",
      },
    ],
  },

  templates: {
    label: "03 / launch lanes",
    h2: "Start with the kind of work you have.",
    intro:
      "coogo home now routes visitors into the same creator language used inside Studio: application, document, report, or revision.",
    all: "Open Library",
    live: "ready",
    preview: "start",
    webMobile: "coogo Studio",
    items: [
      { name: "Make an application", cat: "Generated app workspace" },
      { name: "Transform a document", cat: "DOCX, HWPX, PPTX, XLSX" },
      { name: "Create a report", cat: "Source-backed tables and charts" },
      { name: "Revise existing work", cat: "Proposal and approval loop" },
    ],
  },

  builtOn: {
    label: "04 / collaboration",
    h2: "People and agents work on the same artifact.",
    intro:
      "The interface is intentionally line-based: user, source, coogo, artifact, and review stay visually connected without exposing internal machinery by default.",
    taglines: ["request", "understand", "source", "artifact", "verified"],
  },

  faq: {
    label: "05 / questions",
    h2: "What visitors need to understand first.",
    items: [
      {
        q: "Is coogo for apps or documents?",
        a: "Both. The product promise is living applications and living documents: coogo creates, changes, reviews, shares, and exports both kinds of work from the same Studio loop.",
      },
      {
        q: "What happens after I type into the landing input?",
        a: "coogo stores the request for the Studio handoff, opens the workspace, and keeps the conversation connected to the artifact instead of putting the prompt into the URL.",
      },
      {
        q: "Can agents change my accepted work silently?",
        a: "No. The creator-facing contract is proposal first. coogo explains the change, shows the target, and waits for Use, Revise, or Keep current when approval is required.",
      },
      {
        q: "Where do checks and developer diagnostics go?",
        a: "They stay available as details, checks, history, or developer diagnostics. The default view leads with the artifact, request, review decision, and delivery path.",
      },
      {
        q: "Can I export native files?",
        a: "Yes. Document and report work can target native formats such as DOCX, HWPX, PPTX, XLSX, and PDF, with readiness shown per target.",
      },
      {
        q: "What should the first screen communicate?",
        a: "That coogo can make or change an application, document, or report through a conversation with a visible artifact. Everything else supports that story.",
      },
    ],
  },

  cta: {
    label: "06 / start",
    h2: { pre: "Bring coogo ", accent: "the work you want alive." },
    body: "Start with a request, a source file, or a rough report idea. Studio carries it forward into the artifact and the next decision.",
    start: "Open Studio",
    browse: "Browse Library",
    chips: ["application", "document", "report", "approval loop"],
  },

  pricing: {
    label: "§ PRICING",
    h1: { line1: "One price,", line2: "living outputs." },
    subhead:
      "Apps, documents, and reports ship with review, sharing, and export included.",
    billing: { monthly: "Monthly", annual: "Annual — save 20%" },
    perMonth: "/mo",
    customPrice: "Custom",
    contactForPricing: "Contact us for pricing",
    billedAnnually: "billed annually",
    billedMonthly: "billed monthly",
    tiers: [
      {
        name: "Hobby",
        tag: "Free forever",
        cta: "Start free",
        bullets: [
          "1 shared output",
          "cogo.xyz subdomain",
          "Community support",
          "100 MB storage",
        ],
      },
      {
        name: "Creator",
        tag: "Most popular",
        cta: "Start 14-day trial",
        bullets: [
          "10 shared outputs",
          "Custom domain",
          "Review workflow",
          "5 GB storage",
          "Email support",
        ],
      },
      {
        name: "Studio",
        tag: "For teams",
        cta: "Start trial",
        bullets: [
          "50 shared outputs",
          "Team workspace (5 seats)",
          "Priority support",
          "25 GB storage",
          "Advanced analytics",
        ],
      },
      {
        name: "Enterprise",
        tag: "Custom",
        cta: "Talk to us",
        bullets: [
          "Unlimited outputs",
          "SSO & SAML",
          "SLA",
          "Dedicated success",
          "Custom contracts",
        ],
      },
    ],
    faqLabel: "FAQ",
    faqTitle: "Questions, before you commit",
    faq: [
      {
        q: "Do apps, documents, and reports count the same?",
        a: "Yes. Each shared output keeps its source, review state, and export path together.",
      },
      {
        q: "Can I use my own domain?",
        a: "Creator and above, yes. Point your domain at coogo and we handle SSL automatically.",
      },
      {
        q: "What happens if I downgrade?",
        a: "Outputs above your new tier go into read-only mode, not deleted. Upgrade anytime to reactivate.",
      },
      {
        q: "Do you charge per artifact share?",
        a: "No. Plans are based on shared outputs, AI credits, storage, and team needs — not a cut of your work.",
      },
    ],
  },

  about: {
    label: "§ ABOUT",
    h1: { line1: "coogo makes work", line2: "you can keep changing." },
    intro:
      "coogo is built around one product belief: AI output should not stop at a draft. The artifact should stay alive, visible, reviewable, and ready to ship or export.",
    timeline: [
      {
        year: "01",
        label: "Intent",
        desc: "A user asks coogo to make or change something real.",
      },
      {
        year: "02",
        label: "Artifact",
        desc: "An app, document, or report becomes the center surface.",
      },
      {
        year: "03",
        label: "Decision",
        desc: "coogo proposes, the user approves, and the version moves forward.",
      },
    ],
    teamLabel: "§ TEAM",
    teamTitle: "Built for people working with agents",
    team: [
      { name: "Creator", role: "Sets intent and approves visible changes" },
      { name: "coogo", role: "Understands sources and proposes next steps" },
      { name: "Reviewer", role: "Checks the artifact before share or export" },
      {
        name: "Operator",
        role: "Keeps trust, readiness, and delivery aligned",
      },
    ],
    contactLabel: "§ CONTACT",
    contactTitle: "Say hello.",
    contactIntro:
      "Press, partnerships, or just wanting to chat — we answer every email.",
    contactGeneral: "General",
    contactPress: "Press",
    contactEnterprise: "Enterprise",
    formName: "Name",
    formNamePlaceholder: "Your name",
    formEmail: "Email",
    formEmailPlaceholder: "you@email.com",
    formMessage: "Message",
    formMessagePlaceholder: "Tell us what you're building…",
    formSend: "Send message",
  },

  templatesPage: {
    label: "§ LIBRARY",
    h1: "Choose a living workspace.",
    subhead:
      "Start from the artifact you need: an application, document, report, or revision loop.",
    cats: [
      "Application",
      "Document",
      "Report",
      "Revision",
      "Source packet",
      "Review",
      "Admin",
      "Productivity",
    ],
  },

  blog: {
    label: "§ JOURNAL",
    h1: "Notes on living software.",
    subhead:
      "Product notes on agents, artifacts, source-backed documents, and the review loop.",
    featuredSuffix: "Featured",
    featuredExcerpt:
      "Why the first screen should not explain internals: it should ask what the user wants coogo to make or change, then keep the artifact in view.",
    minRead: "min",
    posts: [
      { t: "Living applications. Living documents.", tag: "Product" },
      { t: "Designing the coogo agent collaboration surface", tag: "Design" },
      { t: "Why source-backed reports need verification", tag: "Trust" },
      { t: "The approval loop: use, revise, or keep current", tag: "Workflow" },
      { t: "From landing prompt to Studio artifact", tag: "Handoff" },
      {
        t: "How content pages explain the product without internals",
        tag: "Positioning",
      },
    ],
  },

  docs: {
    searchPlaceholder: "Search docs",
    sections: [
      {
        h: "GETTING STARTED",
        items: [
          "Introduction",
          "Create your first artifact",
          "Share or publish",
          "Where to go next",
        ],
      },
      {
        h: "ARTIFACT MODEL",
        items: [
          "Overview",
          "Schema reference",
          "Blocks",
          "Events & flows",
          "Expressions",
        ],
      },
      {
        h: "ACTIONS",
        items: [
          "Overview",
          "Execution modes",
          "Actions catalog",
          "Validator loop",
        ],
      },
      {
        h: "STUDIO",
        items: [
          "Overview",
          "Dashboard",
          "Share",
          "IDE workspace",
          "Theme editor",
        ],
      },
      {
        h: "DESIGN SYSTEM",
        items: ["Overview", "Tokens", "Source model", "Extending the system"],
      },
      {
        h: "ADVANCED REFERENCE",
        items: [
          "Design system page",
          "Components (in progress)",
          "Source model (in progress)",
          "Generation (in progress)",
          "Deployment (in progress)",
          "API Reference (in progress)",
        ],
      },
    ],
    centerLabel: "GETTING STARTED",
    h1: "Build with living artifacts",
    intro:
      "coogo turns user intent and source material into applications, documents, and reports that can be reviewed, changed, shared, exported, and verified.",
    calloutTitle: "New to coogo?",
    calloutBody:
      " Start by describing the work, not the implementation. Studio will keep the conversation attached to the artifact.",
    threeTitle: "Three things to know",
    three: [
      {
        title: "The artifact comes first.",
        body: " The center surface should be the app, document, or report, not a dashboard of internal checks.",
      },
      {
        title: "coogo proposes before applying.",
        body: " Changes are understandable suggestions with Use, Revise, or Keep current decisions.",
      },
      {
        title: "Trust stays visible.",
        body: " Source coverage, readiness, export blockers, and verification are available when the user needs them.",
      },
    ],
    quickStartTitle: "Quick start",
    quickStart: {
      c1: "# 1. Capture intent",
      c2: "# 2. Keep the artifact central",
      c3: "# 3. Decide and deliver",
    },
    next: "NEXT",
    nextLink: "Open Studio ->",
    onThisPage: "ON THIS PAGE",
    toc: ["Introduction", "Three things to know", "Quick start"],
  },

  // /design-system showcase — navigational frame only. Code samples, token
  // tables, component demos, TTL/JSON/className specimens, and the numbers
  // (52 components / 10 patterns / 11 rules) stay as English technical specimens
  // in the page. Tokens (47) and version (v4) are unified with the homepage.
  designSystem: {
    sidebar: {
      groups: {
        foundations: "Foundations",
        system: "System",
        contract: "Contract",
      },
      items: {
        overview: "Overview",
        philosophy: "Philosophy",
        architecture: "Architecture",
        tokens: "Tokens",
        components: "Components",
        patterns: "Patterns",
        rules: "Tailwind Rules",
        ontology: "Source model",
        schema: "JSON Schema",
        prompt: "LLM Prompt",
        samples: "Samples",
      },
      source: "Source",
    },
    heroChip: "coogo Design System · v4",
    heroH1: { line1: "Closed vocabulary,", accent: "open expression." },
    heroSubtitle:
      "One vocabulary across every surface — marketing, IDE, review, dashboard, chat.",
    sections: {
      philosophy: {
        name: "Philosophy",
        h2: "Close the vocabulary. Open the expression.",
        sub: "The first principle of coogo DS.",
      },
      architecture: {
        name: "Architecture",
        h2: "One source, six artifacts.",
        sub: "Editable in YAML. Generated everywhere else.",
      },
      tokens: {
        name: "Tokens",
        h2: "47 tokens, zero drift.",
        sub: "47 tokens, zero drift.",
      },
      components: {
        name: "Components",
        h2: "One button, three worlds.",
        sub: "One button, three worlds.",
      },
      patterns: {
        name: "Patterns",
        h2: "10 archetypes, infinite apps.",
        sub: "10 archetypes, infinite apps.",
      },
      rules: {
        name: "Tailwind Rules",
        h2: "className for skeleton, variant for appearance.",
        sub: "className for skeleton, variant for appearance.",
      },
      ontology: {
        name: "Source model",
        h2: "Design details and checks stay attached.",
        sub: "Developer details stay available without taking over the creator view.",
      },
      schema: {
        name: "JSON Schema",
        h2: "Five files declare one block.",
        sub: "Five files declare one block.",
      },
      prompt: {
        name: "LLM Prompt",
        h2: "Brief → generate → validate → retry.",
        sub: "Brief → generate → validate → retry.",
      },
      samples: {
        name: "Samples",
        h2: "One vocabulary, five blocks.",
        sub: "Same vocabulary, five blocks.",
      },
    },
  },

  // Legacy payment-enabled pricing page (homepage/PricingPage.tsx). Prices +
  // productIds stay structural in lib/products.ts; plan text localized by planType.
  pricingPage: {
    h1: "Pricing",
    subhead:
      "For living apps, documents, and reports that need to stay shareable.",
    currencyNote: "All plans are billed in UAE Dirham (AED).",
    startFree: "Start For Free",
    subscribeMonthly: "Subscribe Monthly",
    subscribeYearly: "Subscribe Yearly",
    perMonth: "/ month",
    perYear: "/ year",
    billingMonthly: "Monthly",
    billingYearly: "Yearly",
    yearlySaveHint: "2 months free",
    yearlySaveLabel: "Save {amount} a year ({pct}% off)",
    reassureFree: "No credit card required",
    reassurePaid: "Cancel anytime · secure checkout",
    errPayment: "Payment failed",
    errNoUrl: "No payment URL received",
    errNotAuth: "Not authenticated",
    plans: {
      free: {
        title: "Free",
        description: "Best suited for validating one living output.",
        features: [
          "1 Shared Output",
          "500 AI Credits / month",
          "Basic Review History",
          "Standard Support",
        ],
        badge: "",
      },
      creator: {
        title: "Creator",
        description:
          "Best suited for sharing several apps, documents, or reports.",
        features: [
          "5 Shared Outputs",
          "5,000 AI Credits / month",
          "Review History",
          "Custom Domain Support",
          "Basic Analytics",
        ],
        badge: "Most Popular",
      },
      business: {
        title: "Business",
        description: "Best suited for teams operating many shared outputs.",
        features: [
          "30 Shared Outputs",
          "20,000 AI Credits / month",
          "Team Review History",
          "Advanced Analytics",
          "Priority Support",
        ],
        badge: "",
      },
    },
    faq: {
      title: "Pricing questions",
      items: [
        {
          q: "What currency are plans billed in?",
          a: "All plans are billed in UAE Dirham (AED). The price you see is the price you're charged.",
        },
        {
          q: "Monthly or yearly — what changes?",
          a: "Yearly billing gives you two months free, about 17% off versus paying month to month. Switch between Monthly and Yearly with the toggle above.",
        },
        {
          q: "Is there a free trial?",
          a: "There is no time-limited trial. The Free plan is free to use with no credit card, so you can build a shared output first and upgrade only when you need more.",
        },
        {
          q: "Can I cancel anytime?",
          a: "Yes. Cancel from your account settings whenever you like — your plan stays active until the end of the current billing period and you won't be charged again. Subscriptions are non-refundable; see our Refund & Cancellation Policy.",
        },
        {
          q: "How do shared outputs work after publishing?",
          a: "Each output keeps its source packet, review trail, and export state together, so teams can keep improving it after it is shared.",
        },
        {
          q: "Can I use my own domain?",
          a: "Yes. Shared outputs publish to a free name.cogo.xyz address by default — connect a custom domain from the Settings panel and point your DNS at our edge.",
        },
        {
          q: "What are AI credits?",
          a: "AI credits are your monthly allowance for AI work — generating and editing apps, documents, and reports. Plans include 500 (Free), 5,000 (Creator), and 20,000 (Business) credits each month.",
        },
      ],
    },
  },

  consent: {
    title: "Payment Agreement",
    description: "Please review and agree to our policies before proceeding.",
    agreeIntro: "I agree to the ",
    and1: " and ",
    and2: " and confirm that I have read the ",
    suffix: ".",
    termsLabel: "Terms and Conditions",
    privacyLabel: "Privacy Policy",
    refundLabel: "Refund & Cancellation Policy",
    cancel: "Cancel",
    processing: "Processing...",
    proceed: "Proceed to Payment",
    close: "Close",
  },
};

export type MktDict = typeof en;
